import { ResponsiveKpis } from '@/components/responsive-kpis';
import { TabStrip } from '@/components/tab-strip';
import { PageActions } from '@/components/page-actions';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { Fragment, useMemo, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import {
  usePerfHaloReportExport,
  useReportingQuery,
  useReportingQueryAll,
} from '@/api/hooks';
import type { ReportQueryRequest, ReportQueryResponse } from '@/api/types';
import { useCan } from '@/auth/capabilities';
import {
  PerfHaloAnalysis,
} from '@/components/perf-halo-analysis';
import {
  AttributionBreakdown,
  formatPerfHaloMetricValue,
} from '@/components/attribution-breakdown';
import { DateFilter, Dropdown, SearchableDropdown } from '@/components/dropdowns';
import { QueryErrorNote } from '@/components/error-panel';
import { PerfHaloTrendChart } from '@/components/perf-halo-trend-chart';
import { ChannelPill, Icons } from '@/components/primitives';
import { ReportingDataControls } from '@/components/reporting-controls';
import { csvFilename, triggerDownload } from '@/lib/csv';
import { fmtDateInput, nyDateInputToMs, todayNyInput } from '@/lib/datetime';
import {
  perfHaloFlowIntegrityError,
  perfHaloIntegrityError,
} from '@/lib/perf-halo-integrity';
import {
  comparePerfHaloMetrics,
  perfHaloMetricValue,
  projectPerfHaloMetrics,
} from '@/lib/perf-halo-metrics';
import type { PerfHaloMetricId, PerfHaloMetrics } from '@/lib/perf-halo-metrics';
import {
  buildPerfHaloAnalysisRequest,
  buildPerfHaloExportRequest,
  buildPerfHaloReportFilters,
  buildPerfHaloReportRequest,
  isPerfHaloAnalysisTab,
  PERF_HALO_TAB_GROUPS,
} from '@/lib/perf-halo-report-request';
import type {
  PerfHaloReportSearch,
  PerfHaloReportSearchPatch,
  PerfHaloReportTab,
} from '@/lib/perf-halo-report-search';
import {
  serializePerfHaloReportSearch,
  updatePerfHaloReportSearch,
} from '@/lib/perf-halo-report-search';
import { buildPerfHaloTrendPoints } from '@/lib/perf-halo-trend';
import {
  reportMetricIsAvailable,
  reportMetricUnavailableReason,
  reportingMetricAvailability,
  currentDayTtdLaneNote,
} from '@/lib/reporting-availability';
import type {
  ReportMetricAvailability,
  ReportMetricFamily,
} from '@/lib/reporting-availability';

import './perf-halo-report.css';

interface PerfHaloReportProps {
  search: PerfHaloReportSearch;
  onSearchChange: (
    patch: PerfHaloReportSearchPatch,
    options?: { replace?: boolean },
  ) => void;
}

interface QueryState {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

type ReportRow = ReportQueryResponse['rows'][number];
type ReportDimensions = ReportRow['dimensions'];

interface PerfHaloRow {
  key: string;
  dimensions: ReportDimensions;
  metrics: PerfHaloMetrics;
}

interface DateState {
  preset: string;
  start: Date;
  end: Date;
}

interface DimensionOption {
  id: string;
  name: string;
  sublabel?: string;
}

const REPORT_TABS: Array<{ id: PerfHaloReportTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'campaign', label: 'By Campaign' },
  { id: 'production', label: 'By Assigned Production' },
  { id: 'channel', label: 'By Channel' },
  { id: 'audience', label: 'By Audience' },
  { id: 'audienceType', label: 'By Audience Type' },
  { id: 'creative', label: 'By Creative' },
  { id: 'haloAnalysis', label: 'Halo Analysis' },
];

const CHANNEL_OPTIONS = [
  { id: 'all', label: 'All channels' },
  { id: 'meta', label: 'Meta' },
  { id: 'ctv', label: 'CTV' },
  { id: 'dsp', label: 'Display' },
];

const AUDIENCE_TYPE_OPTIONS = [
  { id: 'all', label: 'All audience types' },
  { id: 'retargeting', label: 'Retargeting' },
  { id: 'direct', label: 'Direct' },
  { id: 'lookalike', label: 'Lookalike' },
];

const TARGET_OPTIONS = [
  { id: 'all', label: 'All destinations' },
  { id: 'meta', label: 'Meta' },
  { id: 'ttd', label: 'TTD' },
];

const TREND_GROUPS: Array<{
  label: string;
  metrics: Array<{ id: PerfHaloMetricId; label: string }>;
}> = [
  {
    label: 'Volume',
    metrics: [
      { id: 'gross_spend', label: 'Gross spend' },
      { id: 'impressions', label: 'Impressions' },
      { id: 'clicks', label: 'Clicks' },
      { id: 'attributed_conversions', label: 'Tickets' },
      { id: 'attributed_revenue', label: 'Ticket Value' },
    ],
  },
  {
    label: 'Efficiency',
    metrics: [
      { id: 'ctr', label: 'CTR' },
      { id: 'cpm', label: 'CPM' },
      { id: 'cpc', label: 'CPC' },
      { id: 'cpa', label: 'CPA' },
      { id: 'roas', label: 'ROAS' },
    ],
  },
];

const TREND_OPTIONS = TREND_GROUPS.flatMap((group) => (
  group.metrics.map((metric) => ({ ...metric, section: group.label }))
));

const MIX_OPTIONS = [
  { id: 'attributed_revenue', label: 'Ticket Value' },
  { id: 'attributed_conversions', label: 'Tickets' },
  { id: 'gross_spend', label: 'Gross spend' },
];

const METRIC_COLUMNS: Array<{
  id: PerfHaloMetricId;
  label: string;
  family: ReportMetricFamily;
  breakdown?: boolean;
}> = [
  { id: 'gross_spend', label: 'Gross spend', family: 'delivery' },
  { id: 'impressions', label: 'Impressions', family: 'delivery' },
  { id: 'clicks', label: 'Clicks', family: 'delivery' },
  { id: 'ctr', label: 'CTR', family: 'delivery' },
  { id: 'cpm', label: 'CPM', family: 'delivery' },
  { id: 'cpc', label: 'CPC', family: 'delivery' },
  { id: 'attributed_conversions', label: 'Tickets', family: 'attribution', breakdown: true },
  { id: 'attributed_revenue', label: 'Ticket Value', family: 'attribution', breakdown: true },
  { id: 'cpa', label: 'CPA', family: 'roas' },
  { id: 'roas', label: 'ROAS', family: 'roas', breakdown: true },
];

const PRIMARY_IDENTITY_WIDTH = 145;
const SECONDARY_IDENTITY_WIDTH = 80;
const METRIC_WIDTH = 58;
const BREAKDOWN_METRIC_WIDTH = 104;

function metricFamily(metric: PerfHaloMetricId): ReportMetricFamily {
  if (['gross_spend', 'impressions', 'clicks', 'ctr', 'cpm', 'cpc'].includes(metric)) {
    return 'delivery';
  }
  if (metric === 'attributed_conversions' || metric === 'attributed_revenue') {
    return 'attribution';
  }
  return 'roas';
}

function metricCell(metrics: PerfHaloMetrics, metric: PerfHaloMetricId): ReactNode {
  if (metric === 'attributed_conversions' || metric === 'attributed_revenue' || metric === 'roas') {
    return <AttributionBreakdown metrics={metrics} metric={metric}/>;
  }
  return formatPerfHaloMetricValue(metrics, metric);
}

function reportRows(response: ReportQueryResponse | undefined): PerfHaloRow[] {
  return (response?.rows ?? []).map((row) => ({
    key: JSON.stringify(row.dimensions),
    dimensions: row.dimensions,
    metrics: projectPerfHaloMetrics(row.metrics),
  }));
}

function dimensionColumns(tab: Exclude<PerfHaloReportTab, 'haloAnalysis'>): Array<{
  id: string;
  label: string;
  render: (row: PerfHaloRow) => ReactNode;
}> {
  const campaign = {
    id: 'campaign',
    label: 'Campaign',
    render: (row: PerfHaloRow) => row.dimensions.campaign_name
      ?? row.dimensions.campaign_id
      ?? 'Unknown campaign',
  };
  const production = {
    id: 'production',
    label: 'Assigned production',
    render: (row: PerfHaloRow) => row.dimensions.assigned_production_name
      ?? row.dimensions.assigned_production_id
      ?? 'Unknown production',
  };
  const channel = {
    id: 'channel',
    label: 'Channel',
    render: (row: PerfHaloRow) => row.dimensions.channel
      ? <ChannelPill channel={row.dimensions.channel}/>
      : <span className="dim">—</span>,
  };
  switch (tab) {
    case 'overview':
    case 'production':
      return [production];
    case 'campaign':
      return [campaign, production, channel];
    case 'channel':
      return [channel];
    case 'audience':
      return [
        {
          id: 'audience',
          label: 'Audience',
          render: (row) => {
            const name = row.dimensions.audience_name
              ?? row.dimensions.audience_id
              ?? 'Unknown audience';
            const revision = row.dimensions.audience_revision;
            return (
              <span
                title={revision == null ? name : `${name} · Revision ${revision}`}
                aria-label={revision == null ? name : `${name}, revision ${revision}`}
              >
                {name}
              </span>
            );
          },
        },
        {
          id: 'audience-type',
          label: 'Audience type',
          render: (row) => row.dimensions.audience_type ?? '—',
        },
      ];
    case 'audienceType':
      return [{
        id: 'audience-type',
        label: 'Audience type',
        render: (row) => row.dimensions.audience_type ?? '—',
      }];
    case 'creative':
      return [
        {
          id: 'creative',
          label: 'Creative',
          // Name plus the revision that actually delivered these numbers.
          // A roll-out does not bump the campaign's plan revision, so the
          // revision is the only thing separating the version a campaign
          // rolled away from and the one it rolled onto. Rows written before
          // the reporting key carried one show the name alone, which is
          // honest: the revision is unknown, not 1.
          render: (row) => {
            const name = row.dimensions.creative_name
              ?? row.dimensions.creative_id
              ?? 'Unknown / unmapped creative';
            const revision = row.dimensions.creative_revision;
            if (revision == null) return <span title={name}>{name}</span>;
            return (
              <span
                title={`${name} · Revision ${revision}`}
                aria-label={`${name}, revision ${revision}`}
              >
                {name} <span className="muted">v{revision}</span>
              </span>
            );
          },
        },
        {
          id: 'format',
          label: 'Format',
          render: (row) => [
            row.dimensions.creative_media_type,
            row.dimensions.creative_format,
          ].filter(Boolean).join(' · ') || '—',
        },
        production,
      ];
  }
}

function FilterField({
  label,
  children,
  isDefaultSelection = false,
}: {
  label: string;
  children: ReactNode;
  isDefaultSelection?: boolean;
}) {
  return (
    <label className={`perf-halo-filter-field${isDefaultSelection ? ' is-default-selection' : ''}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function optionRows(
  response: ReportQueryResponse | undefined,
  id: keyof ReportDimensions,
  name: keyof ReportDimensions,
  sublabel?: (dimensions: ReportDimensions) => string | undefined,
): DimensionOption[] {
  const options = new Map<string, DimensionOption>();
  for (const row of response?.rows ?? []) {
    const optionId = row.dimensions[id];
    if (typeof optionId !== 'string' || !optionId) continue;
    const optionName = row.dimensions[name];
    options.set(optionId, {
      id: optionId,
      name: typeof optionName === 'string' && optionName ? optionName : optionId,
      sublabel: sublabel?.(row.dimensions),
    });
  }
  return [...options.values()].sort((left, right) => (
    left.name.localeCompare(right.name) || left.id.localeCompare(right.id)
  ));
}

function searchableOptions(options: readonly DimensionOption[]) {
  return options.map((option) => ({
    id: option.id,
    label: option.name,
    sublabel: option.sublabel,
  }));
}

/**
 * A validated shared URL can outlive the currently returned option page (or
 * point at an RBAC-visible entity with no activity in the selected dates).
 * Keep that selection explicit instead of making the trigger look like “All”.
 */
function withSelectedOption(
  options: DimensionOption[],
  selectedId: string | undefined,
  selectedName = selectedId,
  sublabel = 'Selected by shared report link',
): DimensionOption[] {
  if (!selectedId || options.some((option) => option.id === selectedId)) {
    return options;
  }
  return [{ id: selectedId, name: selectedName ?? selectedId, sublabel }, ...options];
}

function metricIsAvailable(
  availability: ReportMetricAvailability,
  metric: PerfHaloMetricId,
): boolean {
  return reportMetricIsAvailable(availability, metricFamily(metric));
}

function Unavailable({ reason }: { reason: string }) {
  return <span className="perf-halo-unavailable" title={reason}>—</span>;
}

function PerfHaloKpis({
  response,
  availability,
}: {
  response: ReportQueryResponse;
  availability: ReportMetricAvailability;
}) {
  const metrics = projectPerfHaloMetrics(response.totals);
  const kpis: Array<{ metric: PerfHaloMetricId; label: string }> = [
    { metric: 'gross_spend', label: 'Gross spend' },
    { metric: 'impressions', label: 'Impressions' },
    { metric: 'clicks', label: 'Clicks' },
    { metric: 'attributed_conversions', label: 'Tickets' },
    { metric: 'attributed_revenue', label: 'Ticket Value' },
    { metric: 'roas', label: 'ROAS' },
  ];
  return (
    <ResponsiveKpis><div className="kpi-strip perf-halo-kpis" aria-label="Perf + Halo summary">
      {kpis.map(({ metric, label }) => {
        const available = metricIsAvailable(availability, metric);
        const family = metricFamily(metric);
        return (
          <div className="kpi" key={metric}>
            <div className="kpi-label">{label}</div>
            <div className="kpi-val">
              {!available
                ? <Unavailable reason={reportMetricUnavailableReason(availability, family)}/>
                : metric === 'attributed_conversions' || metric === 'attributed_revenue' || metric === 'roas'
                  ? <AttributionBreakdown metrics={metrics} metric={metric}/>
                  : formatPerfHaloMetricValue(metrics, metric)}
            </div>
          </div>
        );
      })}
    </div></ResponsiveKpis>
  );
}

function TrendPanel({
  response,
  search,
  onSearchChange,
}: {
  response: ReportQueryResponse | undefined;
  search: PerfHaloReportSearch;
  onSearchChange: PerfHaloReportProps['onSearchChange'];
}) {
  const points = useMemo(
    () => buildPerfHaloTrendPoints(response, search.from, search.to),
    [response, search.from, search.to],
  );
  return (
    <section className="perf-halo-panel perf-halo-trend-panel" aria-labelledby="perf-halo-trend-title">
      <div className="perf-halo-panel-heading">
        <div>
          <h2 id="perf-halo-trend-title">Performance over time</h2>
          <p>Daily aggregate for the current report filters.</p>
        </div>
        <div className="perf-halo-metric-select">
          <span>Metric</span>
          <Dropdown
            label=""
            value={search.trendMetric}
            options={TREND_OPTIONS}
            onChange={(value: string) => onSearchChange(
              { trendMetric: value as PerfHaloReportSearch['trendMetric'] },
            )}
            ariaLabel="Perf + Halo trend metric"
            width={190}
            rightAlign
          />
        </div>
      </div>
      <PerfHaloTrendChart
        points={points}
        metric={search.trendMetric}
        ariaLabel="Daily Perf + Halo report"
      />
    </section>
  );
}

function sortableHeader(
  column: { id: PerfHaloMetricId; label: string },
  search: PerfHaloReportSearch,
  onSearchChange: PerfHaloReportProps['onSearchChange'],
) {
  const active = search.sortField === column.id;
  const ariaSort = active
    ? search.sortDirection === 'asc' ? 'ascending' : 'descending'
    : 'none';
  return (
    <th
      key={column.id}
      className={`perf-halo-number perf-halo-metric-column${['attributed_conversions', 'attributed_revenue', 'roas'].includes(column.id) ? ' is-breakdown' : ''}`}
      aria-sort={ariaSort}
    >
      <button
        type="button"
        className="perf-halo-sort"
        onClick={() => onSearchChange({
          sortField: column.id,
          sortDirection: active && search.sortDirection === 'desc' ? 'asc' : 'desc',
        })}
      >
        {column.label}
        <span aria-hidden="true">{active ? (search.sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}</span>
      </button>
    </th>
  );
}

function PerfHaloTable({
  title,
  tab,
  response,
  query,
  search,
  onSearchChange,
}: {
  title: string;
  tab: Exclude<PerfHaloReportTab, 'haloAnalysis'>;
  response: ReportQueryResponse | undefined;
  query: QueryState;
  search: PerfHaloReportSearch;
  onSearchChange: PerfHaloReportProps['onSearchChange'];
}) {
  const availability = reportingMetricAvailability('performance', response?.freshness);
  const dimensions = dimensionColumns(tab);
  const rows = useMemo(() => reportRows(response).sort((left, right) => (
    comparePerfHaloMetrics(
      left.metrics,
      right.metrics,
      search.sortField,
      search.sortDirection,
      left.key,
      right.key,
    )
  )), [response, search.sortDirection, search.sortField]);
  const dimensionWidths = dimensions.map((_, index) => (
    index === 0 ? PRIMARY_IDENTITY_WIDTH : SECONDARY_IDENTITY_WIDTH
  ));
  const dimensionOffsets = dimensionWidths.map((_, index) => (
    dimensionWidths.slice(0, index).reduce((sum, width) => sum + width, 0)
  ));
  const metricWidths = METRIC_COLUMNS.map((column) => (
    column.breakdown ? BREAKDOWN_METRIC_WIDTH : METRIC_WIDTH
  ));
  const tableWidth = dimensionWidths.reduce((sum, width) => sum + width, 0)
    + metricWidths.reduce((sum, width) => sum + width, 0);
  const columnCount = dimensions.length + METRIC_COLUMNS.length;
  return (
    <section className="perf-halo-panel perf-halo-table-panel" aria-labelledby={`perf-halo-table-${tab}`}>
      <div className="perf-halo-panel-heading">
        <div>
          <h2 id={`perf-halo-table-${tab}`}>{title}</h2>
          <p>{rows.length.toLocaleString()} {rows.length === 1 ? 'row' : 'rows'} · sorted by {METRIC_COLUMNS.find((item) => item.id === search.sortField)?.label}</p>
        </div>
      </div>
      <div className="perf-halo-table-scroll" tabIndex={0} aria-label={`${title} metrics`}>
        <table className="tbl perf-halo-table" style={{ width: '100%', minWidth: tableWidth }}>
          <colgroup>
            {dimensions.map((column, index) => (
              <col key={column.id} style={{ width: dimensionWidths[index] }}/>
            ))}
            {METRIC_COLUMNS.map((column, index) => (
              <col key={column.id} style={{ width: metricWidths[index] }}/>
            ))}
          </colgroup>
          <thead>
            <tr>
              {dimensions.map((column, index) => (
                <th
                  key={column.id}
                  className="perf-halo-sticky-identity perf-halo-identity"
                  style={{
                    left: dimensionOffsets[index],
                    width: dimensionWidths[index],
                    minWidth: dimensionWidths[index],
                    maxWidth: dimensionWidths[index],
                  }}
                >
                  {column.label}
                </th>
              ))}
              {METRIC_COLUMNS.map((column) => sortableHeader(column, search, onSearchChange))}
            </tr>
          </thead>
          <tbody>
            {query.isError ? (
              <tr><td colSpan={columnCount}><QueryErrorNote error={query.error} what={title} onRetry={() => void query.refetch()}/></td></tr>
            ) : query.isLoading ? (
              <tr><td colSpan={columnCount} className="perf-halo-empty">Loading report…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={columnCount} className="perf-halo-empty">No rows match the current report filters.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.key}>
                {dimensions.map((column, index) => (
                  <td
                    key={column.id}
                    className="perf-halo-sticky-identity perf-halo-identity"
                    style={{
                      left: dimensionOffsets[index],
                      width: dimensionWidths[index],
                      minWidth: dimensionWidths[index],
                      maxWidth: dimensionWidths[index],
                    }}
                  >
                    {column.render(row)}
                  </td>
                ))}
                {METRIC_COLUMNS.map((column) => (
                  <td
                    key={column.id}
                    className={`perf-halo-number perf-halo-metric-column${column.breakdown ? ' is-breakdown' : ''}`}
                  >
                    {metricIsAvailable(availability, column.id)
                      ? metricCell(row.metrics, column.id)
                      : <Unavailable reason={reportMetricUnavailableReason(availability, column.family)}/>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ChannelMix({
  response,
  metric,
  onMetricChange,
}: {
  response: ReportQueryResponse | undefined;
  metric: PerfHaloReportSearch['channelMixMetric'];
  onMetricChange: (metric: PerfHaloReportSearch['channelMixMetric']) => void;
}) {
  const rows = reportRows(response).filter((row) => row.dimensions.channel);
  const availability = reportingMetricAvailability('performance', response?.freshness);
  const available = metricIsAvailable(availability, metric);
  const magnitude = available
    ? rows.reduce((sum, row) => sum + Math.abs(perfHaloMetricValue(row.metrics, metric) ?? 0), 0)
    : 0;
  const sorted = [...rows].sort((left, right) => (
    Math.abs(perfHaloMetricValue(right.metrics, metric) ?? 0)
      - Math.abs(perfHaloMetricValue(left.metrics, metric) ?? 0)
      || left.key.localeCompare(right.key)
  ));
  const supportMetric = (
    row: PerfHaloRow,
    support: 'attributed_conversions' | 'attributed_revenue' | 'roas',
  ) => metricIsAvailable(availability, support)
    ? <AttributionBreakdown metrics={row.metrics} metric={support} showShare={false}/>
    : <Unavailable reason={reportMetricUnavailableReason(availability, metricFamily(support))}/>;
  return (
    <section className="perf-halo-panel" aria-labelledby="perf-halo-channel-title">
      <div className="perf-halo-panel-heading">
        <div>
          <h2 id="perf-halo-channel-title">Channel mix</h2>
          <p>Bar share uses absolute activity; signed corrections retain their direction.</p>
        </div>
        <div className="seg" aria-label="Channel mix metric">
          {MIX_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={metric === option.id ? 'on' : ''}
              aria-pressed={metric === option.id}
              onClick={() => onMetricChange(option.id as PerfHaloReportSearch['channelMixMetric'])}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="perf-halo-channel-list">
        {!available ? (
          <div className="perf-halo-empty">{reportMetricUnavailableReason(availability, metricFamily(metric))}</div>
        ) : sorted.length === 0 ? (
          <div className="perf-halo-empty">No channel activity in this scope.</div>
        ) : sorted.map((row) => {
          const value = perfHaloMetricValue(row.metrics, metric) ?? 0;
          const percent = magnitude > 0 ? Math.abs(value) / magnitude * 100 : 0;
          const primary = metric === 'attributed_conversions' || metric === 'attributed_revenue'
            ? <AttributionBreakdown metrics={row.metrics} metric={metric}/>
            : formatPerfHaloMetricValue(row.metrics, metric);
          return (
            <div className="perf-halo-channel-row" key={row.key}>
              <div className="perf-halo-channel-name"><ChannelPill channel={row.dimensions.channel!}/></div>
              <div className="perf-halo-channel-bar"><span style={{ width: `${percent}%` }}/></div>
              <div className="perf-halo-channel-primary">{primary}</div>
              <div className="perf-halo-channel-support">
                <div><span>Tickets</span>{supportMetric(row, 'attributed_conversions')}</div>
                <div><span>Ticket Value</span>{supportMetric(row, 'attributed_revenue')}</div>
                <div><span>ROAS</span>{supportMetric(row, 'roas')}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ActiveFilterChips({
  search,
  productionOptions,
  campaignOptions,
  audienceOptions,
  creativeOptions,
  onSearchChange,
}: {
  search: PerfHaloReportSearch;
  productionOptions: DimensionOption[];
  campaignOptions: DimensionOption[];
  audienceOptions: DimensionOption[];
  creativeOptions: DimensionOption[];
  onSearchChange: PerfHaloReportProps['onSearchChange'];
}) {
  const name = (options: DimensionOption[], id: string | undefined) => (
    options.find((option) => option.id === id)?.name ?? id
  );
  const chips: Array<{ label: string; clear: PerfHaloReportSearchPatch }> = [];
  if (search.productionId) chips.push({ label: `Production: ${name(productionOptions, search.productionId)}`, clear: { productionId: undefined } });
  if (search.campaignId) chips.push({ label: `Campaign: ${name(campaignOptions, search.campaignId)}`, clear: { campaignId: undefined } });
  if (search.target) chips.push({ label: `Destination: ${search.target.toUpperCase()}`, clear: { target: undefined } });
  if (search.channel) chips.push({ label: `Channel: ${search.channel.toUpperCase()}`, clear: { channel: undefined } });
  if (search.audienceId) chips.push({ label: `Audience: ${name(audienceOptions, `${search.audienceId}:${search.audienceRevision}`)}`, clear: { audienceId: undefined, audienceRevision: undefined } });
  if (search.audienceType) chips.push({ label: `Audience type: ${search.audienceType}`, clear: { audienceType: undefined } });
  if (search.creativeId) chips.push({ label: `Creative: ${name(creativeOptions, search.creativeId)}`, clear: { creativeId: undefined } });
  if (!chips.length) return null;
  const clearAll: PerfHaloReportSearchPatch = {
    productionId: undefined,
    campaignId: undefined,
    target: undefined,
    channel: undefined,
    audienceId: undefined,
    audienceRevision: undefined,
    audienceType: undefined,
    creativeId: undefined,
  };
  return (
    <div className="perf-halo-filter-chips" aria-label="Active report filters">
      {chips.map((chip) => (
        <button type="button" key={chip.label} onClick={() => onSearchChange(chip.clear)}>
          {chip.label}<span aria-hidden="true"> ×</span>
        </button>
      ))}
      <button type="button" className="perf-halo-clear-all" onClick={() => onSearchChange(clearAll)}>Clear all</button>
    </div>
  );
}

export function PerfHaloReport({ search, onSearchChange }: PerfHaloReportProps) {
  const canExport = useCan('reports_export');
  const todayNy = todayNyInput();
  const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const filters = useMemo(() => buildPerfHaloReportFilters(search), [search]);
  const emptyFilters = useMemo(() => buildPerfHaloReportFilters({
    ...search,
    productionId: undefined,
    campaignId: undefined,
    target: undefined,
    channel: undefined,
    audienceId: undefined,
    audienceRevision: undefined,
    audienceType: undefined,
    creativeId: undefined,
  }), [search]);
  const date = useMemo<DateState>(() => ({
    preset: 'custom',
    start: new Date(nyDateInputToMs(search.from)),
    end: new Date(nyDateInputToMs(search.to)),
  }), [search.from, search.to]);

  const optionRequest = (groupBy: ReportQueryRequest['group_by']) => (
    buildPerfHaloReportRequest(search, groupBy, emptyFilters, 500, false)
  );
  const campaignOptionsQuery = useReportingQueryAll(optionRequest(['campaign']));
  const productionOptionsQuery = useReportingQueryAll(optionRequest(['assigned_production']));
  const audienceOptionsQuery = useReportingQueryAll(optionRequest(['audience']));
  const creativeOptionsQuery = useReportingQueryAll(optionRequest(['creative']));

  const campaignOptions = useMemo(
    () => withSelectedOption(
      optionRows(campaignOptionsQuery.data, 'campaign_id', 'campaign_name'),
      search.campaignId,
    ),
    [campaignOptionsQuery.data, search.campaignId],
  );
  const productionOptions = useMemo(
    () => withSelectedOption(
      optionRows(productionOptionsQuery.data, 'assigned_production_id', 'assigned_production_name'),
      search.productionId,
    ),
    [productionOptionsQuery.data, search.productionId],
  );
  const creativeOptions = useMemo(
    () => withSelectedOption(
      optionRows(
        creativeOptionsQuery.data,
        'creative_id',
        'creative_name',
        (dimensions) => [dimensions.creative_media_type, dimensions.creative_format].filter(Boolean).join(' · ') || undefined,
      ),
      search.creativeId,
    ),
    [creativeOptionsQuery.data, search.creativeId],
  );
  const audienceOptions = useMemo<DimensionOption[]>(() => {
    const options = new Map<string, DimensionOption>();
    for (const row of audienceOptionsQuery.data?.rows ?? []) {
      const id = row.dimensions.audience_id;
      const revision = row.dimensions.audience_revision;
      if (!id || revision == null) continue;
      const key = `${id}:${revision}`;
      options.set(key, {
        id: key,
        name: row.dimensions.audience_name ?? id,
        sublabel: `Revision ${revision}${row.dimensions.audience_type ? ` · ${row.dimensions.audience_type}` : ''}`,
      });
    }
    const available = [...options.values()].sort((left, right) => left.name.localeCompare(right.name) || left.id.localeCompare(right.id));
    const selectedId = search.audienceId && search.audienceRevision != null
      ? `${search.audienceId}:${search.audienceRevision}`
      : undefined;
    return withSelectedOption(
      available,
      selectedId,
      search.audienceId,
      search.audienceRevision == null
        ? 'Selected by shared report link'
        : `Revision ${search.audienceRevision} · Selected by shared report link`,
    );
  }, [audienceOptionsQuery.data, search.audienceId, search.audienceRevision]);

  const trendRequest = useMemo(
    () => buildPerfHaloReportRequest(search, ['day'], filters, 500, false),
    [filters, search],
  );
  const tableRequest = useMemo(() => (
    isPerfHaloAnalysisTab(search.tab)
      ? buildPerfHaloAnalysisRequest(search, filters)
      : buildPerfHaloReportRequest(search, PERF_HALO_TAB_GROUPS[search.tab], filters)
  ), [filters, search]);
  const channelRequest = useMemo(
    () => buildPerfHaloReportRequest(search, ['channel'], filters, 500, false),
    [filters, search],
  );
  const audiencePerformanceRequest = useMemo(
    () => buildPerfHaloReportRequest(search, ['audience_type'], filters, 500, false),
    [filters, search],
  );

  const trendQuery = useReportingQuery(trendRequest);
  const tableQuery = useReportingQueryAll(tableRequest);
  const channelQuery = useReportingQuery(channelRequest, search.tab === 'overview');
  const audiencePerformanceQuery = useReportingQuery(
    audiencePerformanceRequest,
    search.tab === 'haloAnalysis',
  );
  const exportMutation = usePerfHaloReportExport();

  const visibleResponses = [
    trendQuery.data,
    tableQuery.data,
    ...(search.tab === 'overview' ? [channelQuery.data] : []),
    ...(search.tab === 'haloAnalysis' ? [audiencePerformanceQuery.data] : []),
  ];
  const responseIntegrityError = visibleResponses
    .map((response) => perfHaloIntegrityError(response))
    .find((error): error is string => Boolean(error)) ?? null;
  const flowIntegrityError = search.tab === 'haloAnalysis'
    ? perfHaloFlowIntegrityError(tableQuery.data)
    : null;
  const integrityError = responseIntegrityError ?? flowIntegrityError;
  const availability = reportingMetricAvailability('performance', trendQuery.data?.freshness);
  const queryError = [trendQuery, tableQuery, channelQuery, audiencePerformanceQuery]
    .find((query) => query.isError && (
      query === trendQuery
      || query === tableQuery
      || (query === channelQuery && search.tab === 'overview')
      || (query === audiencePerformanceQuery && search.tab === 'haloAnalysis')
    ));
  const baseIntegrityReady = Boolean(trendQuery.data && tableQuery.data && !integrityError);

  const exportActive = () => {
    if (!baseIntegrityReady) return;
    exportMutation.mutate(buildPerfHaloExportRequest(search, filters), {
      onSuccess: (csv) => triggerDownload(
        csvFilename(`perf-halo-${search.tab}`),
        new Blob([csv], { type: 'text/csv;charset=utf-8' }),
      ),
    });
  };

  const tabTitle = REPORT_TABS.find((tab) => tab.id === search.tab)?.label ?? 'Perf + Halo';
  const includesToday = search.from <= todayNy && todayNy <= search.to;
  const ttdLaneNote = currentDayTtdLaneNote(trendQuery.data?.freshness);
  const onTabKeyDown = (
    event: ReactKeyboardEvent<HTMLAnchorElement>,
    index: number,
  ) => {
    if (event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
      return;
    }
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % REPORT_TABS.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + REPORT_TABS.length) % REPORT_TABS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = REPORT_TABS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = tabRefs.current[nextIndex];
    nextTab?.focus();
    nextTab?.click();
  };

  return (
    <div className="perf-halo-report">
      <div className="page-hd perf-halo-page-heading">
        <h1 className="page-title">Perf + Halo</h1>
        <PageActions>
          <ReportingDataControls
            data-phone="meta"
            freshness={trendQuery.data?.freshness}
            dataset="performance"
            highlightAge
          />
          <DateFilter
            completedDaysOnly
            value={date}
            onChange={(range: DateState) => onSearchChange({
              from: fmtDateInput(range.start),
              to: fmtDateInput(range.end),
            })}
          />
          <button
            type="button"
            className="btn btn-icon-phone"
            data-phone="inline"
            aria-label="Export CSV"
            onClick={exportActive}
            disabled={!canExport || exportMutation.isPending || !baseIntegrityReady}
            title={!canExport
              ? 'You do not have permission to export reports'
              : integrityError
                ?? 'Export this filtered view'}
          >
            <span>{Icons.dl}</span><span className="phone-long">{exportMutation.isPending ? 'Exporting…' : 'Export CSV'}</span>
          </button>
        </PageActions>
      </div>

      {integrityError && <div className="perf-halo-integrity-error" role="alert">{integrityError}</div>}
      {queryError && !integrityError && <QueryErrorNote error={queryError.error} what="Perf + Halo report" onRetry={() => void queryError.refetch()}/>}
      {exportMutation.isError && <QueryErrorNote error={exportMutation.error} what="Perf + Halo CSV export" onRetry={exportActive}/>}
      {includesToday && (
        <div className="perf-halo-provisional" role="status">
          Today is included. Current-day delivery is provisional; Tickets, Ticket Value, CPA, and ROAS use completed ET days only.
          {ttdLaneNote && <> {ttdLaneNote}</>}
        </div>
      )}

      <ResponsiveFilters className="perf-halo-filter-bar">
        <FilterField label="Production">
          <SearchableDropdown
            value={search.productionId ?? ''}
            options={searchableOptions(productionOptions)}
            emptyOptionLabel="All productions"
            onChange={(value) => onSearchChange({ productionId: value || undefined })}
            placeholder="All productions"
            searchPlaceholder="Search productions…"
            width="100%"
            size="sm"
          />
        </FilterField>
        <FilterField label="Campaign">
          <SearchableDropdown
            value={search.campaignId ?? ''}
            options={searchableOptions(campaignOptions)}
            emptyOptionLabel="All campaigns"
            onChange={(value) => onSearchChange({ campaignId: value || undefined })}
            placeholder="All campaigns"
            searchPlaceholder="Search campaigns…"
            width="100%"
            size="sm"
          />
        </FilterField>
        <FilterField label="Destination" isDefaultSelection={!search.target}>
          <Dropdown
            label=""
            value={search.target ?? 'all'}
            options={TARGET_OPTIONS}
            onChange={(value: string) => onSearchChange({ target: value === 'all' ? undefined : value as PerfHaloReportSearch['target'] })}
          />
        </FilterField>
        <FilterField label="Channel" isDefaultSelection={!search.channel}>
          <Dropdown
            label=""
            value={search.channel ?? 'all'}
            options={CHANNEL_OPTIONS}
            onChange={(value: string) => onSearchChange({ channel: value === 'all' ? undefined : value as PerfHaloReportSearch['channel'] })}
          />
        </FilterField>
        <FilterField label="Audience / revision">
          <SearchableDropdown
            value={search.audienceId ? `${search.audienceId}:${search.audienceRevision}` : ''}
            options={searchableOptions(audienceOptions)}
            emptyOptionLabel="All audiences"
            onChange={(value) => {
              const match = /^(.*):(\d+)$/.exec(value);
              onSearchChange(match
                ? { audienceId: match[1], audienceRevision: Number(match[2]) }
                : { audienceId: undefined, audienceRevision: undefined });
            }}
            placeholder="All audiences"
            searchPlaceholder="Search audiences…"
            width="100%"
            size="sm"
          />
        </FilterField>
        <FilterField label="Audience type" isDefaultSelection={!search.audienceType}>
          <Dropdown
            label=""
            value={search.audienceType ?? 'all'}
            options={AUDIENCE_TYPE_OPTIONS}
            onChange={(value: string) => onSearchChange({ audienceType: value === 'all' ? undefined : value as PerfHaloReportSearch['audienceType'] })}
          />
        </FilterField>
        <FilterField label="Creative">
          <SearchableDropdown
            value={search.creativeId ?? ''}
            options={searchableOptions(creativeOptions)}
            emptyOptionLabel="All creatives"
            onChange={(value) => onSearchChange({ creativeId: value || undefined })}
            placeholder="All creatives"
            searchPlaceholder="Search creatives…"
            width="100%"
            size="sm"
          />
        </FilterField>
      </ResponsiveFilters>
      <ActiveFilterChips
        search={search}
        productionOptions={productionOptions}
        campaignOptions={campaignOptions}
        audienceOptions={audienceOptions}
        creativeOptions={creativeOptions}
        onSearchChange={onSearchChange}
      />

      <div className="perf-halo-tabs-wrap">
        <TabStrip
          className="report-tabs"
          role="tablist"
          aria-label="Perf + Halo report views"
          aria-orientation="horizontal"
        >
          {REPORT_TABS.map((tab, index) => (
            <Link
              to="/reports/perf-halo"
              search={serializePerfHaloReportSearch(
                updatePerfHaloReportSearch(search, { tab: tab.id }),
              )}
              preload="intent"
              role="tab"
              id={`perf-halo-tab-${tab.id}`}
              aria-controls="perf-halo-tabpanel"
              aria-selected={search.tab === tab.id}
              aria-keyshortcuts="ArrowLeft ArrowRight Home End"
              tabIndex={search.tab === tab.id ? 0 : -1}
              ref={(element) => { tabRefs.current[index] = element; }}
              key={tab.id}
              className={`report-tab ${search.tab === tab.id ? 'on' : ''}`}
              onKeyDown={(event) => onTabKeyDown(event, index)}
            >
              {tab.label}
            </Link>
          ))}
        </TabStrip>
      </div>

      <div
        id="perf-halo-tabpanel"
        role="tabpanel"
        aria-labelledby={`perf-halo-tab-${search.tab}`}
        tabIndex={0}
      >
        {!integrityError && trendQuery.data && (
          <PerfHaloKpis response={trendQuery.data} availability={availability}/>
        )}

        {!integrityError && (
          <div className="perf-halo-content">
            <TrendPanel response={trendQuery.data} search={search} onSearchChange={onSearchChange}/>
            {search.tab === 'overview' ? (
              <Fragment>
                <ChannelMix
                  response={channelQuery.data}
                  metric={search.channelMixMetric}
                  onMetricChange={(channelMixMetric) => onSearchChange({ channelMixMetric })}
                />
                <PerfHaloTable
                  title="Top productions"
                  tab="production"
                  response={tableQuery.data}
                  query={tableQuery}
                  search={search}
                  onSearchChange={onSearchChange}
                />
              </Fragment>
            ) : search.tab === 'haloAnalysis' ? (
              <PerfHaloAnalysis
                flowResponse={tableQuery.data}
                audiencePerformanceResponse={audiencePerformanceQuery.data}
                metric={search.haloMetric}
                onMetricChange={(haloMetric) => onSearchChange({ haloMetric })}
                loading={tableQuery.isLoading || audiencePerformanceQuery.isLoading}
              />
            ) : (
              <PerfHaloTable
                title={tabTitle}
                tab={search.tab}
                response={tableQuery.data}
                query={tableQuery}
                search={search}
                onSearchChange={onSearchChange}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
